import {
  ProductEventBase,
  productEventEmitter,
  ProductEventPayload,
} from "@made-people/grease-product";
import { useEffect } from "react";
import { useMmGtm } from "../mm-gtm-context";
import {
  gtmProductCategory,
  gtmProductParentId,
  gtmProductVariantName,
  gtmGetListId,
} from "../mm-gtm-helpers";

declare global {
  interface Window {
    impressionPushTimer: NodeJS.Timeout;
    mmQueuedProductImpressions: ProductEventBase[];
  }
}

const ITEM_LIMIT = 20;
const IMPRESSION_TIMEOUT = 500;

/**
 * Handles the list event, queues up product impressions, and pushes them to the GTM data layer.
 */
export const MmGtmEventViewItemList = () => {
  const { pusher, frontendContext } = useMmGtm();

  useEffect(() => {
    const handleProductListViewed = (
      params: ProductEventPayload["productViewed"],
    ) => {
      const context = params.context;
      if (context.type === "plp") {
        window.mmQueuedProductImpressions =
          window.mmQueuedProductImpressions || [];
        window.mmQueuedProductImpressions.push(params);

        const flushImpressionQueue = () => {
          pusher({
            event: "productImpressions",
            ecommerce: {
              currency: frontendContext.currencyCode,
              impressions: window.mmQueuedProductImpressions.map((event) => {
                const eventContext = event.context;
                if (eventContext.type !== "plp") {
                  throw new Error(
                    `Expected context type to be 'plp', but got '${eventContext.type}'`,
                  );
                }

                return {
                  id: event.productData.base.productKey,
                  productId: event.productData.base.productKey,
                  parentId: gtmProductParentId(event.productData.base),
                  name: gtmProductParentId(event.productData.base),
                  price: event.productData.purchaseInfo.priceInfo.price.number,
                  category: gtmProductCategory(event.productData.base),
                  list: gtmGetListId(eventContext),
                  position: eventContext.position + 1,
                  title: event.productData.base.content.name,
                  item_variant: gtmProductVariantName(event.productData.base),
                };
              }),
            },
          });
          window.mmQueuedProductImpressions = [];
        };

        clearTimeout(window.impressionPushTimer);

        if (window.mmQueuedProductImpressions.length >= ITEM_LIMIT) {
          // Push immediately if we have enough items to not break the
          // allowed payload body size.
          flushImpressionQueue();
        } else {
          window.impressionPushTimer = setTimeout(
            flushImpressionQueue,
            IMPRESSION_TIMEOUT,
          );
        }
      }
    };

    productEventEmitter.on("productViewed", handleProductListViewed);
    return () => {
      productEventEmitter.off("productViewed", handleProductListViewed);
    };
  }, []);

  return null;
};
